import { useEffect } from "react";

/**
 * Custom hook to hide a menu when the user scrolls within a specified ref.
 *
 * @param {React.RefObject} ref - A ref to the scrollable container element.
 * @param {boolean} isMenuOpen - Controls whether the menu is currently open.
 * @param {Function} onClose - Callback triggered to hide the menu when a scroll event occurs.
 *
 * @example
 * useHideMenuOnScroll(listRef, menuState.open, handleCloseMenu);
 *
 * Whenever the user scrolls the element referenced by `ref` and the menu is open,
 * this hook will call `onClose` to close the menu. Useful for context menus or tooltip overlays.
 */
export function useHideOnScroll(ref, isMenuOpen, onClose) {
  useEffect(() => {
    if (!ref?.current) return;

    const handleScroll = () => {
      if (isMenuOpen) {
        onClose();
      }
    };

    const node = ref.current;
    node.addEventListener("scroll", handleScroll);

    return () => {
      node.removeEventListener("scroll", handleScroll);
    };
  }, [ref, isMenuOpen, onClose]);
}